"use client";

import { Box, Button, Card, CardContent, Chip, Grid, IconButton, Input, Typography } from "@mui/joy";
import { useEffect, useState, useMemo } from "react";
import { ChevronRight, Search, Plus, Minus, ShoppingCart, Package } from "lucide-react";
import { useLabor } from "../../../../hooks/query";
import type {
    IMiniInventory,
    CategoryItemProps,
    CategorySidebarProps,
    CategoryHeaderProps,
    ProductGridProps,
    FooterProps,
    ProductCardProps,
} from "../../../../interfaces";

const DEFAULT_SUPPLIERS = ["1st CR Roofing", "Beacon", "American Roofing Supply"];

function transformLaborData(laborServices: any[]) {
    return {
        Name: "Labor",
        MeasurementType: [],
        Subcategories: (laborServices || []).map((laborItem: any) => ({
            Name: laborItem.Name,
            Manufacturer: "Labor Service",
            Sellers: DEFAULT_SUPPLIERS,
            Unit: laborItem.Unit,
            Description: laborItem.Description,
        })),
    };
}

function buildProductData(materialSchema: any[], laborServices: any[]) {
    const materialsData = (materialSchema || []).map((product: any) => {
        const name = product.Name || product.ProductName || product.name || "";
        const subcategories = (product.Subcategories || []).map((subcategory: any) => ({
            ...subcategory,
            Sellers: subcategory.Sellers || DEFAULT_SUPPLIERS,
        }));

        // Deduplicate subcategories by Name and Manufacturer
        const uniqueSubcategories = subcategories.reduce((acc: any[], subcategory: any) => {
            const existing = acc.find(
                (item) =>
                    item.Name === subcategory.Name &&
                    (item.Manufacturer || item.ManufacturerName) ===
                        (subcategory.Manufacturer || subcategory.ManufacturerName),
            );
            if (!existing) {
                acc.push(subcategory);
            }
            return acc;
        }, []);

        return {
            ...product,
            Name: name,
            Subcategories: uniqueSubcategories,
        };
    });

    const laborData = transformLaborData(laborServices);
    return [...materialsData, laborData];
}

function calculateTotalItems(selectedProducts: any): number {
    if (!selectedProducts) return 0;
    return Object.values(selectedProducts).reduce((total: number, category: any) => {
        return (
            total +
            Object.values(category || {}).reduce((sum: number, item: any) => sum + (item as any).quantity || 0, 0)
        );
    }, 0);
}

function initializeSelectedProducts(selectedValues: any, currentSelectedProducts: any) {
    return Object.entries(selectedValues || {}).reduce(
        (acc: any, [category, values]: [string, any]) => {
            if (acc?.[category]) {
                acc[category][values.value] = { quantity: values.quantity };
                return acc;
            }
            acc = { ...acc, [category]: { [values.value]: { value: values.value, quantity: values.quantity } } };
            return acc;
        },
        { ...currentSelectedProducts },
    );
}

function Header() {
    return (
        <Box
            sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
            }}
        >
            <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                Add more items
            </Typography>
        </Box>
    );
}

function CategoryItem({ category, isSelected, onClick }: CategoryItemProps) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                p: 1.25,
                borderRadius: "lg",
                cursor: "pointer",
                transition: "all 0.18s ease",
                bgcolor: isSelected ? "primary.softBg" : "transparent",
                border: "1px solid",
                borderColor: isSelected ? "primary.softColor" : "divider",
                ":hover": {
                    bgcolor: isSelected ? "primary.softBg" : "background.level1",
                    borderColor: isSelected ? "primary.softColor" : "divider",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: "md",
                    bgcolor: isSelected ? "primary.solidBg" : "background.level2",
                    color: isSelected ? "primary.solidColor" : "text.secondary",
                    flexShrink: 0,
                }}
            >
                <Package size={14} />
            </Box>
            <Typography
                level="body-sm"
                sx={{
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? "primary.plainColor" : "text.primary",
                    flex: 1,
                }}
            >
                {category}
            </Typography>
            {isSelected && <ChevronRight size={14} />}
        </Box>
    );
}

function CategorySidebar({ categories, selectedCategory, onCategoryClick }: CategorySidebarProps) {
    return (
        <Box
            sx={{
                width: 270,
                bgcolor: "background.body",
                borderRight: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                flexShrink: 0,
            }}
        >
            <Box mb={2} flexShrink={0} px={2.5} pt={2}>
                <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 500 }}>
                    Categories
                </Typography>
                <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                    {categories.length} available
                </Typography>
            </Box>
            <Box
                px={2.5}
                pb={3}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    flex: 1,
                    minHeight: 0,
                    pr: 1,
                    "&::-webkit-scrollbar": {
                        width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                        background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        background: "divider",
                        borderRadius: "3px",
                    },
                }}
            >
                {categories.map((category) => (
                    <CategoryItem
                        key={category}
                        category={category}
                        isSelected={category === selectedCategory}
                        onClick={() => onCategoryClick(category)}
                    />
                ))}
            </Box>
        </Box>
    );
}

function CategoryHeader({ selectedProduct }: CategoryHeaderProps) {
    return (
        <Box
            sx={{
                px: 3,
                py: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.body",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flexShrink: 0,
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Box>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                        {selectedProduct ? selectedProduct.Name : "Select a category"}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {selectedProduct
                            ? `${selectedProduct.Subcategories?.length || 0} items available`
                            : "Choose a category from the left to see items."}
                    </Typography>
                </Box>
                {selectedProduct && (
                    <Box display="flex" gap={1.25} alignItems="center" minWidth={260}>
                        <Input
                            placeholder="Search within this category"
                            startDecorator={<Search size={16} />}
                            sx={{
                                flex: 1,
                                bgcolor: "background.surface",
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function EmptyState() {
    return (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
                color: "text.tertiary",
                px: 3,
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    bgcolor: "background.level1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Package size={40} />
            </Box>
            <Typography level="title-md">Select a category to view products</Typography>
            <Typography level="body-sm">Choose from the categories on the left</Typography>
        </Box>
    );
}

function ProductGrid({
    selectedProduct,
    selectedProducts,
    onNewItemToCart,
    onRemoveItemFromCart,
    priceRanges,
}: ProductGridProps) {
    return (
        <Box
            sx={{
                flex: 1,
                overflowY: "auto",
                px: 3,
                py: 3,
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                    background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                    background: "divider",
                    borderRadius: "4px",
                },
            }}
        >
            <Grid container spacing={2}>
                {selectedProduct?.Subcategories?.map((subcategory: any, index: number) => {
                    const uniqueKey = `${subcategory.Name}-${
                        subcategory.Manufacturer || subcategory.ManufacturerName || index
                    }`;
                    return (
                        <Grid xs={12} sm={6} md={4} key={uniqueKey}>
                            <ProductCard
                                cartCount={selectedProducts?.[selectedProduct.Name]?.[subcategory.Name]?.quantity || 0}
                                onNewItemToCart={(event: any) =>
                                    onNewItemToCart({ ...event, category: selectedProduct.Name })
                                }
                                onRemoveItemFromCart={(event: any) =>
                                    onRemoveItemFromCart({ ...event, category: selectedProduct.Name })
                                }
                                name={subcategory.Name}
                                sellers={subcategory.Sellers}
                                manufacturer={subcategory.Manufacturer || subcategory.ManufacturerName}
                                unit={subcategory.Unit}
                                description={subcategory.Description}
                                priceRanges={priceRanges}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
}

function Footer({ totalItemsCount, onContinue }: FooterProps) {
    return (
        <Box
            sx={{
                px: 3,
                py: 2.5,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "background.body",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexShrink: 0,
            }}
        >
            <Box>
                <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 0.5 }}>
                    Items in this selection
                </Typography>
                <Typography level="title-md" sx={{ fontWeight: 600 }}>
                    {totalItemsCount} {totalItemsCount === 1 ? "item" : "items"}
                </Typography>
            </Box>
            <Button
                size="md"
                variant="solid"
                color="primary"
                startDecorator={<ShoppingCart size={18} />}
                onClick={onContinue}
                disabled={totalItemsCount === 0}
                sx={{
                    minWidth: 150,
                    fontWeight: 600,
                }}
            >
                Add to estimate
            </Button>
        </Box>
    );
}

function ProductCard({
    sellers,
    name,
    manufacturer,
    onNewItemToCart,
    onRemoveItemFromCart,
    cartCount,
    unit,
    description,
    priceRanges,
}: ProductCardProps) {
    const hasQuantity = cartCount > 0;
    const priceRange = priceRanges?.[name];
    const priceDisplay = priceRange ? `$${priceRange.min.toFixed(2)} - $${priceRange.max.toFixed(2)}` : null;

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.2s ease",
                border: "1px solid",
                borderColor: hasQuantity ? "neutral.softColor" : "divider",
                bgcolor: hasQuantity ? "neutral.softBg" : "background.surface",
                boxShadow: hasQuantity ? "sm" : "none",
                ":hover": {
                    borderColor: "neutral.plainColor",
                },
            }}
        >
            <CardContent sx={{ p: 0, display: "flex", flexDirection: "column", flex: 1, height: "100%" }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Chip
                        size="sm"
                        variant="soft"
                        color="neutral"
                        sx={{
                            borderRadius: "sm",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                        }}
                    >
                        {sellers.length} {sellers.length === 1 ? "Supplier" : "Suppliers"}
                    </Chip>
                    {hasQuantity && (
                        <Chip
                            size="sm"
                            variant="solid"
                            color="neutral"
                            sx={{
                                borderRadius: "sm",
                                fontWeight: 600,
                            }}
                        >
                            {cartCount} in cart
                        </Chip>
                    )}
                </Box>

                <Typography
                    level="title-md"
                    sx={{
                        fontWeight: 600,
                        mb: 0.25,
                        lineHeight: 1.3,
                        color: "text.primary",
                    }}
                >
                    {name}
                </Typography>

                <Typography
                    level="body-sm"
                    sx={{
                        color: "text.secondary",
                        mb: description ? 0.5 : 0.75,
                    }}
                >
                    by {manufacturer}
                </Typography>

                {description && (
                    <Typography
                        level="body-xs"
                        sx={{
                            color: "text.tertiary",
                            mb: 0.5,
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {description}
                    </Typography>
                )}

                {unit && (
                    <Typography
                        level="body-xs"
                        sx={{
                            color: "text.tertiary",
                            mb: 0.75,
                            fontStyle: "italic",
                        }}
                    >
                        Unit: {unit}
                    </Typography>
                )}

                {priceDisplay && (
                    <Box mb={1} sx={{ mt: "auto" }}>
                        <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 0.25 }}>
                            Price Range
                        </Typography>
                        <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }}>
                            {priceDisplay}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        pt: 1,
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: 1,
                        }}
                    >
                        <IconButton
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            onClick={() => onRemoveItemFromCart({ name, manufacturer })}
                            disabled={cartCount === 0}
                            sx={{
                                borderColor: "divider",
                                "&:hover": {
                                    borderColor: "neutral.plainColor",
                                    bgcolor: "neutral.softBg",
                                },
                                "&:disabled": {
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <Minus size={16} />
                        </IconButton>
                        <Box
                            sx={{
                                minWidth: 50,
                                textAlign: "center",
                                py: 0.5,
                                px: 1,
                                borderRadius: "sm",
                                bgcolor: hasQuantity ? "neutral.softBg" : "background.level1",
                                border: "1px solid",
                                borderColor: hasQuantity ? "neutral.softColor" : "divider",
                            }}
                        >
                            <Typography
                                level="body-md"
                                sx={{
                                    fontWeight: 600,
                                    color: hasQuantity ? "neutral.plainColor" : "text.secondary",
                                }}
                            >
                                {cartCount}
                            </Typography>
                        </Box>
                        <IconButton
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            onClick={() => onNewItemToCart({ name, manufacturer })}
                            sx={{
                                borderColor: "neutral.plainColor",
                                "&:hover": {
                                    bgcolor: "neutral.solidBg",
                                    color: "neutral.solidColor",
                                    borderColor: "neutral.solidBg",
                                },
                            }}
                        >
                            <Plus size={16} />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function MiniInventory({ selectedValues, onContinue, materialSchema, priceRanges }: IMiniInventory) {
    const [productCategories, setProductCategories] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<any>({});

    const { useListLaborServices } = useLabor();
    const { data: laborServices } = useListLaborServices();

    const productData = useMemo(
        () => buildProductData(materialSchema, laborServices || []),
        [materialSchema, laborServices],
    );

    useEffect(() => {
        const categories = Array.from(new Set(productData.map((product) => product.Name)));
        setProductCategories(categories);

        if (categories.length > 0 && !selectedCategory) {
            setSelectedCategory(categories[0]);
        }
    }, [productData]);

    useEffect(() => {
        const initialized = initializeSelectedProducts(selectedValues || {}, {});
        setSelectedProducts(initialized);
    }, [selectedValues]);

    function handleNewItemToCart(event: { category: string; name: string; manufacturer: string }) {
        setSelectedProducts((prev: any) => {
            const categoryData = prev?.[event.category] || {};
            const currentQuantity = categoryData[event.name]?.quantity || 0;

            return {
                ...prev,
                [event.category]: {
                    ...categoryData,
                    [event.name]: { quantity: currentQuantity + 1 },
                },
            };
        });
    }

    function handleRemoveItemFromCart(event: { category: string; name: string; manufacturer: string }) {
        setSelectedProducts((prev: any) => {
            const categoryData = prev?.[event.category];
            if (!categoryData?.[event.name]) {
                console.error("Item not found in cart");
                return prev;
            }

            const newQuantity = categoryData[event.name].quantity - 1;
            if (newQuantity <= 0) {
                const { [event.name]: removed, ...rest } = categoryData;
                return { ...prev, [event.category]: rest };
            }

            return {
                ...prev,
                [event.category]: {
                    ...categoryData,
                    [event.name]: { quantity: newQuantity },
                },
            };
        });
    }

    function handleCategoryClick(category: string) {
        setSelectedCategory(category);
    }

    function handleContinue() {
        onContinue(selectedProducts);
    }

    const selectedProduct = productData.find((product) => product.Name === selectedCategory);
    const totalItemsCount = useMemo(() => calculateTotalItems(selectedProducts), [selectedProducts]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                maxHeight: "100vh",
                height: "100%",
                bgcolor: "background.body",
                overflow: "hidden",
            }}
        >
            <Header />

            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    minHeight: 0,
                    bgcolor: "background.surface",
                    overflow: "hidden",
                }}
            >
                <CategorySidebar
                    categories={productCategories}
                    selectedCategory={selectedCategory}
                    onCategoryClick={handleCategoryClick}
                />

                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <CategoryHeader selectedProduct={selectedProduct} />

                    {selectedProduct ? (
                        <>
                            <ProductGrid
                                selectedProduct={selectedProduct}
                                selectedProducts={selectedProducts}
                                onNewItemToCart={handleNewItemToCart}
                                onRemoveItemFromCart={handleRemoveItemFromCart}
                                priceRanges={priceRanges}
                            />
                            <Footer totalItemsCount={totalItemsCount} onContinue={handleContinue} />
                        </>
                    ) : (
                        <EmptyState />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
